import { RGBColor, converters } from '@yola/yola-palette-generator';

const { hsl2rgb, rgb2hsl } = converters;

const extractStopPosition = (stop) => Number.parseFloat(stop?.length?.value || '0');

const extractStopColor = (stop) => {
  const [h, s, l, a = 1] = (stop?.value || []).map(Number);
  if ([h, s, l, a].some(Number.isNaN)) {
    return null;
  }
  return { h, s, l, a };
};

function interpolateHslColorAtPosition(position, colorStops) {
  const sorted = [...colorStops]
    .filter((stop) => extractStopColor(stop) !== null)
    .sort((a, b) => extractStopPosition(a) - extractStopPosition(b));

  if (sorted.length === 0) {
    return { h: 0, s: 0, l: 0, a: 1 };
  }

  if (position <= extractStopPosition(sorted[0])) {
    return { ...extractStopColor(sorted[0]) };
  }

  const last = sorted[sorted.length - 1];

  if (position >= extractStopPosition(last)) {
    return { ...extractStopColor(last) };
  }

  let left = sorted[0];
  let right = sorted[1];

  for (let i = 1; i < sorted.length; i += 1) {
    if (extractStopPosition(sorted[i]) >= position) {
      left = sorted[i - 1];
      right = sorted[i];
      break;
    }
  }

  const leftPosition = extractStopPosition(left);
  const rightPosition = extractStopPosition(right);
  const span = rightPosition - leftPosition || 1;
  const t = (position - leftPosition) / span;

  const leftColor = extractStopColor(left);
  const rightColor = extractStopColor(right);
  const leftAlpha = leftColor.a;
  const rightAlpha = rightColor.a;

  const leftRgb = hsl2rgb({
    hue: leftColor.h,
    saturation: leftColor.s,
    lightness: leftColor.l,
    alpha: leftAlpha,
  });
  const rightRgb = hsl2rgb({
    hue: rightColor.h,
    saturation: rightColor.s,
    lightness: rightColor.l,
    alpha: rightAlpha,
  });

  const interpR = leftRgb.red + t * (rightRgb.red - leftRgb.red);
  const interpG = leftRgb.green + t * (rightRgb.green - leftRgb.green);
  const interpB = leftRgb.blue + t * (rightRgb.blue - leftRgb.blue);
  const interpA = leftAlpha + t * (rightAlpha - leftAlpha);

  const hsl = rgb2hsl(new RGBColor(interpR, interpG, interpB, interpA));

  return {
    h: Math.round(hsl.hue * 100) / 100,
    s: Math.round(hsl.saturation * 10000) / 10000,
    l: Math.round(hsl.lightness * 10000) / 10000,
    a: Math.round(hsl.alpha * 1000) / 1000,
  };
}

export { interpolateHslColorAtPosition };
